import { Box, Button, Chip, Typography, CircularProgress } from '@mui/material';
import PhoneIcon from '@mui/icons-material/Phone';
import LockIcon from '@mui/icons-material/Lock';
import TipsAndUpdatesIcon from '@mui/icons-material/TipsAndUpdates';
import { motion } from 'framer-motion';

const PRIMARY = 'rgb(11, 90, 240)';
const NAVY = '#0a2540';

export type LeadCardState = 'priority-locked' | 'available' | 'claiming' | 'claimed';

export interface LeadCardVisualProps {
  date?: string;
  firstname: string;
  lastname: string;
  phone: string;
  state: LeadCardState;
  timerSeconds?: number;
  highlight?: boolean;
  /** Force l'affichage du nom et téléphone en clair, même hors état claimed */
  reveal?: boolean;
  /** Affiche un sous-titre explicatif sous le CTA (uniquement pour priority-locked) */
  lockedHint?: string;
}

const BlurredSuffix = ({ children }: { children: React.ReactNode }) => (
  <Box
    component="span"
    sx={{
      filter: 'blur(3px)',
      opacity: 0.8,
      userSelect: 'none',
      display: 'inline',
    }}
  >
    {children}
  </Box>
);

const formatTimer = (seconds: number): string => {
  const h = Math.floor(seconds / 3600);
  const m = Math.floor((seconds % 3600) / 60);
  if (h > 0 && m > 0) {
    return `${h}h ${m} min`;
  }
  if (h > 0) {
    return `${h}h`;
  }
  return `${m} min`;
};

const LeadCardVisual = ({
  date,
  firstname,
  lastname,
  phone,
  state,
  timerSeconds,
  highlight = false,
  reveal = false,
  lockedHint,
}: LeadCardVisualProps) => {
  const isLocked = state === 'priority-locked';
  const isClaimed = state === 'claimed';
  const isClaiming = state === 'claiming';
  const showFull = isClaimed || reveal;

  const lastnameVisible = showFull ? lastname : lastname.slice(0, 3);
  const lastnameBlurred = showFull ? '' : lastname.slice(3);
  const phoneVisible = showFull ? phone : phone.slice(0, 5);
  const phoneBlurred = showFull ? '' : phone.slice(5);

  return (
    <Box
      component={motion.div}
      animate={
        highlight
          ? {
              boxShadow: [
                '0 0 0 0px rgba(59,130,246,0)',
                '0 0 0 4px rgba(59,130,246,0.28)',
                '0 0 0 2px rgba(59,130,246,0.14)',
              ],
            }
          : { boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }
      }
      transition={{ duration: 1.6, repeat: highlight ? Infinity : 0, repeatType: 'loop' }}
      sx={{
        position: 'relative',
        border: '1px solid',
        borderColor: highlight ? 'rgba(59,130,246,0.4)' : '#e5e7eb',
        borderRadius: 2,
        p: 2,
        bgcolor: '#fff',
        width: '100%',
        maxWidth: 360,
        overflow: 'hidden',
      }}
    >
      {date && (
        <Chip
          label={date}
          size="small"
          sx={{
            position: 'absolute',
            top: 8,
            left: 8,
            height: 20,
            fontSize: 10,
            fontWeight: 600,
            bgcolor: '#f4f7fa',
            border: '1px solid #e0e6eb',
            color: '#667788',
            '.MuiChip-label': { px: 1 },
          }}
        />
      )}

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mt: date ? 3 : 0 }}>
        <Box sx={{ flex: 1, minWidth: 0 }}>
          <Typography
            sx={{
              fontSize: 13,
              fontWeight: 700,
              color: NAVY,
              lineHeight: 1.3,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {firstname} {lastnameVisible}
            {lastnameBlurred && <BlurredSuffix>{lastnameBlurred}</BlurredSuffix>}
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              color: '#475569',
              lineHeight: 1.3,
              fontVariantNumeric: 'tabular-nums',
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
            }}
          >
            {phoneVisible}
            {phoneBlurred && <BlurredSuffix>{phoneBlurred}</BlurredSuffix>}
          </Typography>
        </Box>

        <Box sx={{ flexShrink: 0, display: 'flex', alignItems: 'center' }}>
          {isLocked && (
            <Button
              variant="outlined"
              size="small"
              disabled
              startIcon={<LockIcon sx={{ fontSize: '14px !important' }} />}
              sx={{
                textTransform: 'none',
                fontSize: 11,
                fontWeight: 700,
                px: 1.5,
                py: 0.5,
                borderRadius: 1.5,
                minWidth: 0,
                fontVariantNumeric: 'tabular-nums',
              }}
            >
              {formatTimer(timerSeconds ?? 5400)}
            </Button>
          )}
          {state === 'available' && (
            <Button
              variant="contained"
              size="small"
              startIcon={<PhoneIcon sx={{ fontSize: '14px !important' }} />}
              sx={{
                textTransform: 'none',
                fontSize: 11,
                fontWeight: 700,
                px: 1.5,
                py: 0.5,
                borderRadius: 1.5,
                bgcolor: PRIMARY,
                minWidth: 0,
                '&:hover': { bgcolor: '#1e40af' },
              }}
            >
              Rappeler
            </Button>
          )}
          {isClaiming && (
            <Button
              variant="contained"
              size="small"
              disabled
              sx={{
                textTransform: 'none',
                fontSize: 11,
                fontWeight: 700,
                px: 1.5,
                py: 0.5,
                borderRadius: 1.5,
                minWidth: 80,
              }}
            >
              <CircularProgress size={12} sx={{ color: '#fff', mr: 0.8 }} />
              ...
            </Button>
          )}
          {isClaimed && (
            <Button
              variant="contained"
              size="small"
              startIcon={<TipsAndUpdatesIcon sx={{ fontSize: '14px !important' }} />}
              sx={{
                textTransform: 'none',
                fontSize: 11,
                fontWeight: 700,
                px: 1.5,
                py: 0.5,
                borderRadius: 1.5,
                bgcolor: '#dbeafe',
                color: '#1e40af',
                minWidth: 0,
                boxShadow: 'none',
                '&:hover': { bgcolor: '#bfdbfe', boxShadow: 'none' },
              }}
            >
              Instructions d&apos;appel
            </Button>
          )}
        </Box>
      </Box>

      {isLocked && lockedHint && (
        <Box
          sx={{
            mt: 1.5,
            pt: 1.5,
            borderTop: '1px solid #f1f5f9',
            textAlign: 'center',
          }}
        >
          <Typography sx={{ fontSize: 11, color: '#94a3b8', fontWeight: 500 }}>
            {lockedHint}
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LeadCardVisual;
